import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Convert Google Drive sharing links to direct image URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return url;
  
  // Already converted
  if (url.includes('lh3.googleusercontent.com/d/')) return url;
  
  // Format: drive.google.com/file/d/<ID>/view
  const match1 = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (match1 && match1[1]) return `https://lh3.googleusercontent.com/d/${match1[1]}=s2000`;
  
  // Format: drive.google.com/open?id=<ID>
  const match2 = url.match(/[?&]id=([^&]+)/);
  if (url.includes('drive.google.com/open') && match2 && match2[1]) {
    return `https://lh3.googleusercontent.com/d/${match2[1]}=s2000`;
  }
  
  // Format: drive.google.com/uc?id=<ID> or export=view&id=<ID>
  const match3 = url.match(/drive\.google\.com\/uc\?.*id=([^&]+)/);
  if (match3 && match3[1]) return `https://lh3.googleusercontent.com/d/${match3[1]}=s2000`;
  
  return url;
};

const NewsManager = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags: '',
    image_url: '',
    published: true
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (newsItem = null) => {
    if (newsItem) {
      setEditingNews(newsItem);
      setFormData({
        title: newsItem.title,
        excerpt: newsItem.excerpt,
        content: newsItem.content,
        category: newsItem.category,
        tags: newsItem.tags.join(', '),
        image_url: newsItem.image_url || '',
        published: newsItem.published
      });
    } else {
      setEditingNews(null);
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        category: '',
        tags: '',
        image_url: '',
        published: true
      });
    }
    setDialogOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      image_url: convertGoogleDriveUrl(formData.image_url) // Convert Google Drive URLs
    };

    try {
      const token = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingNews) {
        await axios.put(`${API}/news/${editingNews.id}`, payload, config);
        toast.success('News updated successfully');
      } else {
        await axios.post(`${API}/news`, payload, config);
        toast.success('News created successfully');
      }

      setDialogOpen(false);
      fetchNews();
    } catch (error) {
      console.error('Error saving news:', error);
      toast.error('Failed to save news');
    }
  };

  const handleDelete = async (newsId) => {
    if (!window.confirm('Are you sure you want to delete this news article?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/news/${newsId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('News deleted successfully');
      fetchNews();
    } catch (error) {
      console.error('Error deleting news:', error);
      toast.error('Failed to delete news');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">News Management</h2>
          <p className="text-gray-600">Create, edit, and manage news articles</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          data-testid="add-news-btn"
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
        >
          <Plus className="mr-2" size={20} />
          Add News
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {news.map((item) => (
            <div key={item.id} data-testid={`news-item-${item.id}`} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <span className="px-3 py-1 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full">
                    {item.category}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenDialog(item)}
                      data-testid={`edit-news-${item.id}`}
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      data-testid={`delete-news-${item.id}`}
                      className="text-red-600 hover:text-red-800 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.excerpt}</p>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{new Date(item.created_at).toLocaleDateString()}</span>
                  <span className={`px-2 py-1 rounded text-xs ${item.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {item.published ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingNews ? 'Edit News' : 'Add News'}</DialogTitle>
            <DialogDescription>
              {editingNews ? 'Update the news article details' : 'Create a new news article'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} data-testid="news-form" className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                name="title"
                data-testid="input-title"
                value={formData.title}
                onChange={handleChange}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="category">Category *</Label>
              <Input
                id="category"
                name="category"
                data-testid="input-category"
                value={formData.category}
                onChange={handleChange}
                required
                className="mt-1"
                placeholder="e.g., Announcements, Updates, Events"
              />
            </div>

            <div>
              <Label htmlFor="excerpt">Excerpt *</Label>
              <Textarea
                id="excerpt"
                name="excerpt"
                data-testid="input-excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                required
                rows={2}
                className="mt-1"
                placeholder="Brief summary of the news article"
              />
            </div>

            <div>
              <Label htmlFor="content">Content *</Label>
              <Textarea
                id="content"
                name="content"
                data-testid="input-content"
                value={formData.content}
                onChange={handleChange}
                required
                rows={6}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                name="tags"
                data-testid="input-tags"
                value={formData.tags}
                onChange={handleChange}
                className="mt-1"
                placeholder="e.g., employment, benefits, policy"
              />
            </div>

            <div>
              <Label htmlFor="image_url">Image URL</Label>
              <Input
                id="image_url"
                name="image_url"
                data-testid="input-image-url"
                value={formData.image_url}
                onChange={handleChange}
                className="mt-1"
                placeholder="Paste Google Drive link or direct image URL"
              />
              <p className="text-xs text-gray-500 mt-1">
                Supports: Direct URLs or Google Drive links (drive.google.com/file/d/...)
              </p>
              {formData.image_url && (
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Preview:</p>
                  <img 
                    src={convertGoogleDriveUrl(formData.image_url)} 
                    alt="Preview" 
                    className="max-h-32 rounded object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="published"
                name="published"
                data-testid="input-published"
                checked={formData.published}
                onChange={handleChange}
                className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
              />
              <Label htmlFor="published" className="cursor-pointer">Publish immediately</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                data-testid="submit-news-btn"
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                {editingNews ? 'Update News' : 'Create News'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsManager;
